import { Fragment, Slice } from '@atlaskit/editor-prosemirror/model';

import type {
	RemoveNodeCommand,
	StreamingCommandFactory,
} from '../aiStreamingOrchestratorPluginType';
import { findNodeByLocalId } from './utils/find-node-by-local-id';

export const createRemoveNodeCommand: StreamingCommandFactory<RemoveNodeCommand> =
	(api) => (command) => {
		return api.core.actions.execute(({ tr }) => {
			return findNodeByLocalId(api, tr, command.localId, ({ node, pos }) => {
				api.aiExperience.actions.tagAsAIStreamingTransformation(tr, { isFinal: true });

				// Record a coarse "remove" segment so the Post Stream
				// Review modal can review / undo / redo this deletion. removeNode is
				// non-streamable: it mutates the doc directly here and never reaches
				// `buildCommitTransaction`, so we must record the segment ourselves.
				// The removed node becomes `originalUserContentSlice`. Deleting the
				// only paragraph inserts the schema-required empty paragraph, which
				// must be included in the replacement span for review undo/redo.
				// `recordAIContentSegments` is a no-op outside Review Moment.
				const removedSlice = new Slice(Fragment.from(node), 0, 0);
				const wasOnlyParagraph =
					pos === 0 &&
					node.type === tr.doc.type.schema.nodes.paragraph &&
					node.nodeSize === tr.doc.content.size;
				tr.delete(pos, pos + node.nodeSize).scrollIntoView();
				// Map the original position through the delete step to get the
				// collapsed (zero-width) position at the deletion point.
				const collapsedPos = tr.mapping.map(pos);
				const requiredParagraph =
					wasOnlyParagraph &&
					tr.doc.childCount === 1 &&
					tr.doc.firstChild?.type === tr.doc.type.schema.nodes.paragraph &&
					tr.doc.firstChild.content.size === 0
						? tr.doc.firstChild
						: null;
				api.aiExperience.actions.recordAIContentSegments(tr, [
					{
						startPos: requiredParagraph ? 0 : collapsedPos,
						endPos: requiredParagraph ? requiredParagraph.nodeSize : collapsedPos,
						originalUserContentSlice: removedSlice,
						newSlice: requiredParagraph
							? new Slice(Fragment.from(requiredParagraph), 0, 0)
							: Slice.empty,
						kind: 'remove',
					},
				]);
				api.aiExperience.actions.stampAgentEditChrome(tr);
			});
		});
	};
